import { AdminHeader } from "@/components/AdminHeader";
import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { MessageCircle, Bell, DollarSign, Pencil, Undo2, Landmark, Plus } from "lucide-react";

const STATUS_STYLES = {
  confirmed: "text-emerald-700 border-emerald-200 bg-emerald-50",
  pending_payment: "text-amber-700 border-amber-200 bg-amber-50",
  cancelled: "text-red-700 border-red-200 bg-red-50",
  completed: "text-sky-700 border-sky-200 bg-sky-50",
};

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

function TollCell({ b, act }) {
  const [val, setVal] = useState(b.pi_toll_amount ?? "");
  const [trips, setTrips] = useState(b.pi_toll_trips ?? "");
  return (
    <div data-testid={`toll-cell-${b.id}`}>
      <div className="flex items-center gap-1.5">
        <Input
          data-testid={`toll-trips-${b.id}`}
          type="number"
          min="0"
          step="1"
          value={trips}
          onChange={(e) => setTrips(e.target.value)}
          placeholder="Trips"
          className="input-dark h-8 w-16 text-xs"
        />
        <Button
          data-testid={`toll-trips-add-${b.id}`}
          size="sm"
          variant="outline"
          onClick={() => act(b.id, { pi_toll_trips: parseInt(trips || "0", 10) || 0 }, `Toll pass applied — ${parseInt(trips || "0", 10) || 0} bridge trip(s): $8.99 service + $3.00 per trip on invoice`)}
          className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] hover:text-[#C71E2E] font-ui h-8 px-3 text-xs"
        >
          Toll pass
        </Button>
      </div>
      {Number(b.pi_toll_trips) > 0 && (
        <p data-testid={`toll-trips-saved-${b.id}`} className="text-xs text-emerald-600 font-ui mt-1">{b.pi_toll_trips} trip{Number(b.pi_toll_trips) === 1 ? "" : "s"} × $3.00 + $8.99 service = {fmtMoney(b.pi_toll_amount)}</p>
      )}
      <div className="flex items-center gap-1.5 mt-1.5">
        <Input
          data-testid={`toll-input-${b.id}`}
          type="number"
          min="0"
          step="0.01"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="$0.00"
          className="input-dark h-8 w-24 text-xs"
        />
        <Button
          data-testid={`toll-save-${b.id}`}
          size="sm"
          variant="outline"
          onClick={() => act(b.id, { pi_toll_amount: parseFloat(val || "0") }, "PI bridge toll saved — now on client invoice")}
          className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] hover:text-[#C71E2E] font-ui h-8 px-3 text-xs"
        >
          Save
        </Button>
      </div>
      {Number(b.pi_toll_amount) > 0 && !Number(b.pi_toll_trips) && (
        <p data-testid={`toll-saved-${b.id}`} className="text-xs text-emerald-600 font-ui mt-1">{fmtMoney(b.pi_toll_amount)} on invoice</p>
      )}
    </div>
  );
}

function EvidencePicker({ b, act }) {
  const [photos, setPhotos] = useState(null);
  const [sel, setSel] = useState(Array.isArray(b.charge_evidence) ? b.charge_evidence : []);
  const base = process.env.REACT_APP_BACKEND_URL;
  const loadPhotos = async () => {
    if (photos !== null) return;
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/photo-compare`);
      setPhotos([...(data.pickup || []), ...(data.return || [])]);
    } catch (e) {
      toast.error(errMsg(e));
      setPhotos([]);
    }
  };
  const toggle = (p) => setSel((s) => (s.some((x) => x.url === p.url) ? s.filter((x) => x.url !== p.url) : [...s, { url: p.url, label: p.label }]));
  return (
    <details data-testid={`evidence-${b.id}`} className="border border-[#E5DED5] rounded-lg px-2.5 py-1.5" onToggle={(e) => { if (e.target.open) loadPhotos(); }}>
      <summary className="cursor-pointer text-[#C71E2E] font-semibold select-none">
        Evidence photos {sel.length > 0 ? `(${sel.length} selected ✓)` : "(pick for charge emails)"}
      </summary>
      <div className="mt-2">
        {photos === null ? (
          <p className="text-muted-foreground">Loading photos…</p>
        ) : photos.length === 0 ? (
          <p className="text-muted-foreground">No photos on file for this booking.</p>
        ) : (
          <div className="grid grid-cols-3 gap-1.5">
            {photos.map((p, i) => {
              const on = sel.some((x) => x.url === p.url);
              return (
                <button key={p.url} type="button" data-testid={`evidence-photo-${b.id}-${i}`} aria-pressed={on} onClick={() => toggle(p)} className={`relative rounded-lg overflow-hidden border-2 transition-all ${on ? "border-[#C71E2E] ring-2 ring-[#C71E2E]/30" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={base + p.url} alt={p.label} className="w-full h-14 object-cover" loading="lazy" />
                  {on && <span className="absolute top-0.5 right-0.5 bg-[#C71E2E] text-white rounded-full w-4 h-4 text-[10px] leading-4 text-center">✓</span>}
                </button>
              );
            })}
          </div>
        )}
        <Button data-testid={`evidence-save-${b.id}`} size="sm" variant="outline" className="mt-2 rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui h-7 px-2.5 text-[11px]" onClick={() => act(b.id, { charge_evidence: sel }, `Evidence saved — ${sel.length} photo${sel.length === 1 ? "" : "s"} embedded in charge emails`)}>
          Save evidence
        </Button>
        <p className="mt-1 text-[10px] text-muted-foreground leading-relaxed">Selected photos are embedded as evidence in penalty &amp; deposit-charge emails. With none selected, all inspection photos are included.</p>
      </div>
    </details>
  );
}

function PenaltiesCell({ b, act, cap = 0 }) {
  const [gal, setGal] = useState(b.fuel_gallons ?? "");
  const [lateH, setLateH] = useState(b.late_hours ?? (Number(b.late_accrual_hours) > 0 ? b.late_accrual_hours : ""));
  const btn = "rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] hover:text-[#C71E2E] font-ui h-7 px-2.5 text-[11px]";
  return (
    <div data-testid={`penalties-cell-${b.id}`}>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <Button
            data-testid={`smoking-fee-${b.id}`}
            size="sm"
            variant="outline"
            className={btn}
            onClick={() => act(b.id, { smoking_fee: Number(b.smoking_fee) > 0 ? 0 : 150 }, Number(b.smoking_fee) > 0 ? "Smoking penalty removed" : "Smoking penalty $150 added to invoice")}
          >
            {Number(b.smoking_fee) > 0 ? "✓ Smoking $150" : "+ Smoking $150"}
          </Button>
          <Button
            data-testid={`cleaning-fee-${b.id}`}
            size="sm"
            variant="outline"
            className={btn}
            onClick={() => act(b.id, { cleaning_fee: Number(b.cleaning_fee) > 0 ? 0 : 30 }, Number(b.cleaning_fee) > 0 ? "Cleaning fee removed" : "Cleaning fee $30 added to invoice")}
          >
            {Number(b.cleaning_fee) > 0 ? "✓ Cleaning $30" : "+ Cleaning $30"}
          </Button>
          <Button
            data-testid={`airport-return-fee-${b.id}`}
            size="sm"
            variant="outline"
            className={btn}
            onClick={() => act(b.id, { airport_return_fee: Number(b.airport_return_fee) > 0 ? 0 : 25 }, Number(b.airport_return_fee) > 0 ? "Airport return fee removed" : "Airport return fee $25 added to invoice")}
          >
            {Number(b.airport_return_fee) > 0 ? "✓ Airport return $25" : "+ Airport return $25"}
          </Button>
          <Button
            data-testid={`key-fob-fee-${b.id}`}
            size="sm"
            variant="outline"
            className={btn}
            onClick={() => {
              if (Number(b.key_fob_fee) > 0) { act(b.id, { key_fob_fee: 0 }, "Key fob fee removed"); return; }
              const amt = window.prompt("Key fob fee — missing or damaged key (shown on the guest invoice):", "250");
              if (amt === null) return;
              act(b.id, { key_fob_fee: parseFloat(amt) || 250 }, `Key fob fee $${(parseFloat(amt) || 250).toFixed(2)} added to invoice`);
            }}
          >
            {Number(b.key_fob_fee) > 0 ? `✓ Key fob ${fmtMoney(b.key_fob_fee)}` : "+ Key fob"}
          </Button>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            data-testid={`fuel-return-level-${b.id}`}
            defaultValue=""
            className="input-dark h-7 text-xs rounded-md border border-[#E5DED5] px-1.5 bg-transparent"
            onChange={(e) => {
              const ret = e.target.value;
              if (!ret) return;
              const FRAC = { Full: 1, "3/4": 0.75, "1/2": 0.5, "1/4": 0.25 };
              const outF = FRAC[b.fuel_level] ?? 1;
              if (!cap) { toast.error("Set this vehicle's tank capacity in the Vehicles tab first"); e.target.value = ""; return; }
              const missing = Math.round((outF - FRAC[ret]) * cap * 10) / 10;
              if (missing <= 0) { toast.error("No fuel missing at that level"); e.target.value = ""; return; }
              act(b.id, { fuel_gallons: missing }, `Fuel charge saved — ${missing} gal @ $8/gal = $${(missing * 8).toFixed(2)} on invoice`);
              e.target.value = "";
            }}
          >
            <option value="">Returned at…</option>
            <option value="3/4">3/4 tank</option>
            <option value="1/2">1/2 tank</option>
            <option value="1/4">1/4 tank</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <Input data-testid={`fuel-gallons-${b.id}`} type="number" min="0" step="0.1" value={gal} onChange={(e) => setGal(e.target.value)} placeholder="Fuel gal" className="input-dark h-7 w-20 text-xs" />
          <Button
            data-testid={`fuel-save-${b.id}`}
            size="sm"
            variant="outline"
            className={btn}
            onClick={() => act(b.id, { fuel_gallons: parseFloat(gal || "0") }, `Fuel charge saved — ${gal || 0} gal @ $8/gal on invoice`)}
          >
            $8/gal
          </Button>
        </div>
        <div className="flex items-center gap-1.5">
          <Input data-testid={`late-hours-${b.id}`} type="number" min="0" step="0.5" value={lateH} onChange={(e) => setLateH(e.target.value)} placeholder="Late hrs" className="input-dark h-7 w-20 text-xs" />
          <Button
            data-testid={`late-save-${b.id}`}
            size="sm"
            variant="outline"
            className={btn}
            onClick={() => act(b.id, { late_hours: parseFloat(lateH || "0") }, `Late fee saved — ${lateH || 0} hr @ $25/hr on invoice`)}
          >
            $25/hr
          </Button>
        </div>
        <EvidencePicker b={b} act={act} />
        {Number(b.fuel_charge) > 0 && <p data-testid={`fuel-saved-${b.id}`} className="text-xs text-emerald-600 font-ui">{fmtMoney(b.fuel_charge)} fuel on invoice</p>}
        {Number(b.late_fee) > 0 && <p data-testid={`late-saved-${b.id}`} className="text-xs text-emerald-600 font-ui">{fmtMoney(b.late_fee)} late fee on invoice</p>}
        {!(Number(b.late_fee) > 0) && Number(b.late_accrual_hours) > 0 && <p data-testid={`late-accrual-${b.id}`} className="text-xs text-amber-600 font-ui">Auto: {b.late_accrual_hours}h overdue → {fmtMoney(b.late_accrual_fee)} accruing (pre-filled above)</p>}
      </div>
    </div>
  );
}

function ComparePhotos({ bookingId, bookingRef, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/admin/bookings/${bookingId}/photo-compare`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  }, [bookingId]);
  const base = process.env.REACT_APP_BACKEND_URL;
  const Col = ({ title, photos, empty }) => (
    <div>
      <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">{title}</p>
      <div className="mt-3 space-y-3">
        {photos.length === 0 && <p className="text-xs font-ui text-[#4A4A4A]">{empty}</p>}
        {photos.map((p) => (
          <figure key={p.url}>
            <a href={base + p.url} target="_blank" rel="noopener noreferrer">
              <img src={base + p.url} alt={p.label} className="w-full rounded-lg border border-[#E5DED5] object-cover max-h-56 hover:opacity-90 transition-opacity" loading="lazy" />
            </a>
            <figcaption className="mt-1 text-[11px] font-ui text-[#4A4A4A]">{p.label} · {new Date(p.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
  return (
    <div data-testid="photo-compare-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl tracking-tight text-[#1A1A1A]">Before / After — {bookingRef}</h3>
          <Button data-testid="photo-compare-close" size="sm" variant="outline" onClick={onClose} className="rounded-full border-[#E5DED5] font-ui">Close</Button>
        </div>
        {err && <p className="mt-4 text-sm text-destructive font-ui">{err}</p>}
        {!data && !err && <p className="mt-6 text-sm font-ui text-[#4A4A4A]">Loading photos…</p>}
        {data && (
          <>
            {data.damage_notes && (
              <p data-testid="compare-damage-notes" className="mt-4 text-sm font-ui text-[#1A1A1A] bg-[#FAF6F0] border border-[#E5DED5] rounded-xl px-4 py-3">
                Renter's pickup notes: {data.damage_notes}
              </p>
            )}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Col title="Pickup (renter inspection)" photos={data.pickup} empty="No pickup inspection photos for this booking." />
              <Col title="Return (renter photos)" photos={data.return} empty="No return photos submitted yet." />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const OUTSTANDING = (b) =>
  (Number(b.pi_toll_amount) || 0) + (Number(b.smoking_fee) || 0) + (Number(b.cleaning_fee) || 0) + (Number(b.fuel_charge) || 0) + (Number(b.late_fee) || 0) + (Number(b.cancellation_fee) || 0) + (Number(b.airport_return_fee) || 0) + (Number(b.key_fob_fee) || 0) - (Number(b.charges_paid_amount) || 0);

const MONEY_LABELS = {
  cancellation_fee: "Cancellation fee applied",
  extension_payment: "Extension payment recorded",
  charge_capture: "Card on file charged",
  refund: "Refund issued",
  deposit_hold: "Deposit hold placed",
  deposit_release: "Deposit hold released",
  deposit_charge: "Deposit captured",
  deposit_deduct: "Deposit deducted",
  charges_added: "Post-rental charges added",
  refund_reversal: "Refund mark removed",
};

const METHOD_LABELS = { card: "Card (Stripe)", paypal: "PayPal", zelle: "Zelle", wire: "Bank wire", manual_card: "Card (manual)", manual: "Manual" };

const PAY_STATUS_LABELS = { paid: "Paid", awaiting: "Awaiting payment", refunded: "Refunded" };

const payStatus = (b) => (b.payment_state === "refunded" ? "refunded" : (b.status === "confirmed" || b.status === "completed" || (b.status === "cancelled" && b.paid_at) ? "paid" : "awaiting"));

function historyOf(b) {
  const e = [];
  const push = (at, label) => { if (at) e.push({ at, label }); };
  push(b.created_at, "Booking created");
  push(b.paid_at, `Payment confirmed${b.payment_method ? ` via ${b.payment_method}` : ""}`);
  push(b.contract_signed_at, `Contract signed${b.contract_signer ? ` by ${b.contract_signer}` : ""}`);
  push(b.picked_up_at, `Vehicle picked up${b.picked_up_by ? ` by ${b.picked_up_by}` : ""}`);
  push(b.cancelled_at, `Cancelled${b.cancelled_by ? ` by ${b.cancelled_by}` : ""}`);
  push(b.uncancelled_at, `Cancellation undone — restored${b.uncancelled_by ? ` by ${b.uncancelled_by}` : ""}`);
  push(b.invoice_reopened_at, `Invoice re-opened${b.invoice_reopened_by ? ` by ${b.invoice_reopened_by}` : ""}`);
  push(b.deposit_released_at, "Deposit released");
  push(b.deposit_charged_at, `Deposit charged${b.deposit_applied_amount != null ? ` — $${Number(b.deposit_applied_amount).toFixed(2)} applied, $${Number(b.deposit_released_back || 0).toFixed(2)} released back` : ""}${b.deposit_note ? ` — ${b.deposit_note}` : ""}`);
  (b.penalty_charges || []).forEach((c) => push(c.at, `Penalty $${Number(c.amount).toFixed(2)} — ${c.reason || "post-rental charges"}${c.by ? ` by ${c.by}` : ""}${c.reversed ? " — REFUNDED" : ""}`));
  push(b.cancellation_fee_at, `20% cancellation fee applied ($${Number(b.cancellation_fee || 0).toFixed(2)})`);
  (b.date_changes || []).forEach((d) => e.push({ at: d.at, dateChange: true, label: d.undo
    ? `Date change undone — reverted to ${d.to}${d.price_delta ? ` (${d.price_delta > 0 ? "+" : "−"}$${Math.abs(d.price_delta).toFixed(2)})` : ""}${d.by ? ` by ${d.by}` : ""}`
    : `Dates changed ${d.from} → ${d.to}${d.price_delta ? ` (${d.price_delta > 0 ? "+" : "−"}$${Math.abs(d.price_delta).toFixed(2)})` : ""}${d.by ? ` by ${d.by}` : ""}` }));
  (b.hour_extensions || []).forEach((h) => push(h.at, `Extended ${h.hours} hr @ $25/hr ($${Number(h.amount).toFixed(2)})${h.by ? ` by ${h.by}` : ""}`));
  (b.payment_method_history || []).forEach((m) => push(m.at, `Payment method changed ${METHOD_LABELS[m.from] || m.from} → ${METHOD_LABELS[m.to] || m.to}${m.by ? ` by ${m.by}` : ""}`));
  (b.wire_proof_events || []).forEach((w) => push(w.at, `Wire payment proof uploaded (${w.filename || "file"}) by ${w.by === "guest" ? "guest" : w.by || "admin"}`));
  (b.payment_status_history || []).forEach((p) => push(p.at, `Payment status changed ${PAY_STATUS_LABELS[p.from] || p.from} → ${PAY_STATUS_LABELS[p.to] || p.to}${p.by ? ` by ${p.by}` : ""}`));
  push(b.completed_at, "Rental completed");
  return e.sort((x, y) => String(x.at).localeCompare(String(y.at)));
}

function moneyEntry(m) {
  return {
    at: m.created_at,
    label: `${MONEY_LABELS[m.action] || m.action}${m.amount != null ? ` — $${Number(m.amount).toFixed(2)}` : ""}${m.detail ? ` (${m.detail})` : ""}${m.actor ? ` by ${m.actor}` : ""}`,
    money: true,
  };
}

function mergedHistory(b, money) {
  return [...historyOf(b), ...(money || []).map(moneyEntry)].sort((x, y) => String(x.at).localeCompare(String(y.at)));
}

function BookingHistory({ b }) {
  const items = historyOf(b);
  const [money, setMoney] = useState(null);
  const onToggle = async (e) => {
    if (e.currentTarget.open && money === null) {
      try {
        const { data } = await api.get(`/admin/bookings/${b.id}/financial-audit`);
        setMoney(data);
      } catch { setMoney([]); }
    }
  };
  if (!items.length) return null;
  const all = mergedHistory(b, money);
  return (
    <details data-testid={`booking-history-${b.id}`} className="mt-1.5" onToggle={onToggle}>
      <summary className="cursor-pointer text-[10px] font-ui uppercase tracking-wide text-muted-foreground hover:text-[#C71E2E]">History ({all.length}{money === null ? "+" : ""})</summary>
      <ul className="mt-1.5 space-y-1 border-l-2 border-[#E5DED5] pl-3 max-w-[260px]">
        {all.map((h, i) => (
          <li key={i} data-testid={`booking-history-item-${b.id}-${i}`} className={`text-[10px] font-ui leading-snug ${h.money ? "text-[#8f1f2b]" : "text-[#4A4A4A]"}`}>
            <span className="text-muted-foreground">{String(h.at).slice(0, 16).replace("T", " ")}</span> — {h.money && <span className="font-semibold">$ </span>}{h.label}
          </li>
        ))}
      </ul>
    </details>
  );
}

async function quickCloseBooking(b, reload, setBusy, deduct = false) {
  const out = Math.max(0, OUTSTANDING(b));
  const dep = Number(b.deposit_amount) || 150;
  const applied = Math.min(dep, out);
  const msg = deduct
    ? `Quick Close + Deduct: verify the return photos as OK, record fuel as Full, deduct $${applied.toFixed(2)} of outstanding penalty fees from the $${dep.toFixed(0)} deposit (remainder $${(dep - applied).toFixed(2)} released) and complete the rental — all in one go?\n\nIf fuel is short or a different amount should be charged, use the individual buttons instead.`
    : "Quick Close: verify the return photos as OK, record fuel as Full (no fuel charge), release the $150 deposit and complete the rental — all in one go?\n\nIf fuel is short or the deposit should be charged, use the individual buttons instead.";
  if (!window.confirm(msg)) return;
  setBusy(true);
  try {
    await api.patch(`/admin/bookings/${b.id}`, { return_photos_verified: true });
    if (!b.fuel_return_level) await api.patch(`/admin/bookings/${b.id}`, { fuel_return_level: "Full" });
    if ((b.deposit_status || "held") === "held") await api.patch(`/admin/bookings/${b.id}`, { deposit_action: deduct ? "deduct" : "release", ...(deduct ? { deposit_note: "Penalty fees deducted from security deposit" } : {}) });
    await api.patch(`/admin/bookings/${b.id}`, { status: "completed" });
    toast.success(deduct ? `Rental closed — photos verified, $${applied.toFixed(2)} deducted from deposit, completion email & text sent` : "Rental closed — photos verified, deposit released, completion email & text sent");
    reload();
  } catch (e) {
    toast.error(errMsg(e));
    reload();
  } finally {
    setBusy(false);
  }
}

function TodaysPickups({ bookings }) {
  const today = new Date().toLocaleDateString("en-CA");
  const due = bookings.filter((b) => b.status === "confirmed" && b.start_date === today);
  if (!due.length) return null;
  return (
    <div data-testid="todays-pickups" className="mb-6 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-4">
      <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A]">
        Today's pickups — {due.length} guest{due.length > 1 ? "s" : ""} arriving
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {due.map((b) => {
          const ready = b.vehicle_status === "ready";
          const preparing = !ready && (b.vehicle_status === "preparing" || b.rental_started_at);
          return (
            <div key={b.id} data-testid={`todays-pickup-${b.id}`} className="border border-[#E5DED5] rounded-xl px-3.5 py-2.5 flex flex-wrap items-center gap-2.5 text-xs font-ui">
              <div className="mr-1">
                <p className="font-semibold text-[#1A1A1A]">{b.vehicle_name}</p>
                <p className="text-muted-foreground">{b.booking_ref} · {b.customer_name}</p>
              </div>
              {b.picked_up_at ? (
                <span data-testid={`todays-pickedup-${b.id}`} className="border rounded-full px-2 py-0.5 text-[10px] font-ui text-emerald-700 border-emerald-200 bg-emerald-50">picked up ✓</span>
              ) : (
              <span data-testid={`todays-prep-${b.id}`} className={`border rounded-full px-2 py-0.5 text-[10px] font-ui ${ready ? "text-emerald-700 border-emerald-200 bg-emerald-50" : preparing ? "text-amber-700 border-amber-200 bg-amber-50" : "text-muted-foreground border-[#E5DED5]"}`}>
                {ready ? "vehicle ready ✓" : preparing ? "being prepared…" : "waiting for scan"}
              </span>
              )}
              {!b.contract_signed_at && (
                <span data-testid={`todays-contract-${b.id}`} className="border rounded-full px-2 py-0.5 text-[10px] font-ui text-amber-700 border-amber-200 bg-amber-50">contract unsigned</span>
              )}
              <Link data-testid={`todays-scan-${b.id}`} to={`/admin/scan/${b.id}`} className="inline-flex items-center gap-1 text-[#C71E2E] hover:underline underline-offset-2">
                Scan console →
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TodaysReturns({ bookings, reload }) {
  const [busyId, setBusyId] = useState(null);
  const today = new Date().toLocaleDateString("en-CA");
  const due = bookings.filter((b) => b.status === "confirmed" && b.end_date === today);
  if (!due.length) return null;
  const chip = (ok, warn, label, testid) => (
    <span data-testid={testid} className={`border rounded-full px-2 py-0.5 text-[10px] font-ui ${ok ? "text-emerald-700 border-emerald-200 bg-emerald-50" : warn ? "text-amber-700 border-amber-200 bg-amber-50" : "text-muted-foreground border-[#E5DED5]"}`}>{label}</span>
  );
  return (
    <div data-testid="todays-returns" className="mb-6 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-4">
      <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#4A4A4A]">
        Today's returns — {due.length} vehicle{due.length > 1 ? "s" : ""} due back
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {due.map((b) => (
          <div key={b.id} data-testid={`todays-return-${b.id}`} className="border border-[#E5DED5] rounded-xl px-3.5 py-2.5 flex flex-wrap items-center gap-2.5 text-xs font-ui">
            <div className="mr-1">
              <p className="font-semibold text-[#1A1A1A]">{b.vehicle_name}</p>
              <p className="text-muted-foreground">{b.booking_ref} · {b.customer_name}</p>
            </div>
            {!b.return_submitted
              ? chip(false, true, "photos awaiting", `todays-photos-${b.id}`)
              : b.return_photos_verified
                ? chip(true, false, "photos verified ✓", `todays-photos-${b.id}`)
                : chip(false, true, "photos to verify", `todays-photos-${b.id}`)}
            {chip((b.deposit_status || "held") !== "held", (b.deposit_status || "held") === "held", `deposit ${b.deposit_status || "held"}`, `todays-deposit-${b.id}`)}
            {b.return_submitted && !b.return_photos_verified && (
              <Button
                data-testid={`todays-quick-close-${b.id}`}
                size="sm"
                variant="outline"
                disabled={busyId === b.id}
                onClick={() => quickCloseBooking(b, reload, (v) => setBusyId(v ? b.id : null))}
                className="rounded-full border-emerald-300 bg-transparent text-emerald-700 hover:bg-emerald-600 hover:text-white font-ui h-7 px-2.5 text-[11px] disabled:opacity-40"
              >
                {busyId === b.id ? "Closing…" : "Quick Close ✓"}
              </Button>
            )}
            {b.return_submitted && !b.return_photos_verified && (b.deposit_status || "held") === "held" && OUTSTANDING(b) > 0 && (
              <Button
                data-testid={`todays-quick-close-deduct-${b.id}`}
                size="sm"
                variant="outline"
                disabled={busyId === b.id}
                onClick={() => quickCloseBooking(b, reload, (v) => setBusyId(v ? b.id : null), true)}
                className="rounded-full border-amber-300 bg-transparent text-amber-700 hover:bg-amber-600 hover:text-white font-ui h-7 px-2.5 text-[11px] disabled:opacity-40"
              >
                {busyId === b.id ? "Closing…" : "Close + Deduct"}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StaffReturnUpload({ b, reload }) {
  const [files, setFiles] = useState({ vehicle: null, key: null, fuel: null });
  const [busy, setBusy] = useState(false);
  const inp = "text-[10px] file:text-[#C71E2E] file:mr-1 max-w-[150px]";
  const upload = async () => {
    if (!files.vehicle || !files.key || !files.fuel) return toast.error("Add vehicle, key and fuel photos");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("vehicle_photo", files.vehicle);
      fd.append("key_photo", files.key);
      fd.append("fuel_photo", files.fuel);
      await api.post(`/admin/bookings/${b.id}/return-photos`, fd);
      toast.success("Return photos uploaded — verify them, then close the rental");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`return-upload-${b.id}`} className="flex flex-col gap-1">
      {[["vehicle", "Vehicle"], ["key", "Keys"], ["fuel", "Fuel"]].map(([k, l]) => (
        <label key={k} className="flex items-center gap-1.5">
          <span className="w-11 shrink-0 text-muted-foreground">{l}</span>
          <input data-testid={`return-upload-${k}-${b.id}`} type="file" accept="image/*" className={inp} onChange={(e) => setFiles((f) => ({ ...f, [k]: e.target.files?.[0] || null }))} />
          {files[k] && <span className="text-emerald-600">✓</span>}
        </label>
      ))}
      <Button data-testid={`return-upload-btn-${b.id}`} size="sm" variant="outline" disabled={busy} onClick={upload} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui h-7 px-2.5 text-[11px] disabled:opacity-40">
        {busy ? "Uploading…" : "Upload return photos"}
      </Button>
    </div>
  );
}

function ReturnCell({ b, act, cap, focus, reload }) {
  const [showCompare, setShowCompare] = useState(!!focus);
  const [quickClosing, setQuickClosing] = useState(false);
  const [mFuel, setMFuel] = useState("Full");
  const [mKeys, setMKeys] = useState("returned");
  const [mPhotos, setMPhotos] = useState("none");
  const [mBusy, setMBusy] = useState(false);
  const manualClose = async () => {
    if (!window.confirm("Close this rental manually with the details entered? Photo, verification and deposit checks are skipped — use for attended returns.")) return;
    setMBusy(true);
    try {
      await api.patch(`/admin/bookings/${b.id}`, {
        status: "completed",
        skip_return_checks: true,
        fuel_return_level: mFuel,
        keys_returned: mKeys,
        return_no_photos: mPhotos === "none",
      });
      toast.success("Rental closed manually — completion email & text sent");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMBusy(false);
    }
  };
  const btn = "rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] hover:text-[#C71E2E] font-ui h-7 px-2.5 text-[11px]";
  const quickClose = () => quickCloseBooking(b, reload, setQuickClosing);
  if (b.status !== "confirmed" && b.status !== "completed") {
    return <div data-testid={`return-cell-${b.id}`} className="text-muted-foreground text-xs font-ui">Available once the booking is confirmed — fuel level, return photos, deposit and quick-close actions appear here.</div>;
  }
  const depStatus = b.deposit_status || "held";
  return (
    <div data-testid={`return-cell-${b.id}`}>
      <div className="flex flex-col gap-1.5 text-xs font-ui">
        {b.fuel_return_level ? (
          <p data-testid={`return-fuel-${b.id}`} className="text-emerald-600">Fuel back: {b.fuel_return_level} ✓</p>
        ) : (
          <select
            data-testid={`return-fuel-select-${b.id}`}
            defaultValue=""
            className="input-dark h-7 text-xs rounded-md border border-[#E5DED5] px-1.5 bg-transparent"
            onChange={(e) => {
              const ret = e.target.value;
              if (!ret) return;
              if (ret === (b.fuel_level || "Full")) { act(b.id, { fuel_return_level: ret }, "Fuel return recorded — no charge"); return; }
              const FRAC = { Full: 1, "3/4": 0.75, "1/2": 0.5, "1/4": 0.25 };
              const outF = FRAC[b.fuel_level] ?? 1;
              if (!cap) { toast.error("Set this vehicle's tank capacity in the Vehicles tab first"); e.target.value = ""; return; }
              const missing = Math.round((outF - FRAC[ret]) * cap * 10) / 10;
              if (missing <= 0) { toast.error("No fuel missing at that level"); e.target.value = ""; return; }
              act(b.id, { fuel_gallons: missing, fuel_return_level: ret }, `Fuel short ${missing} gal — $${(missing * 8).toFixed(2)} charged to invoice`);
            }}
          >
            <option value="">Fuel back…</option>
            <option value="Full">Full</option>
            <option value="3/4">3/4</option>
            <option value="1/2">1/2</option>
            <option value="1/4">1/4</option>
          </select>
        )}
        {!b.return_submitted ? (
          <>
            <p className="text-muted-foreground">Return photos: awaiting</p>
            <StaffReturnUpload b={b} reload={reload} />
            {b.customer_phone && (
              <a
                data-testid={`return-whatsapp-${b.id}`}
                href={`https://wa.me/${String(b.customer_phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Easy Drive Car Rental — time to return your ${b.vehicle_name} (${b.booking_ref}). Snap 3 quick photos (vehicle condition, key placement, fuel gauge) and share the location — it takes two minutes: ${window.location.origin}/rental-return?ref=${b.booking_ref}&email=${encodeURIComponent(b.customer_email)}`)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[#1FA855] hover:underline underline-offset-2"
              >
                <MessageCircle size={12} /> WhatsApp return link to guest
              </a>
            )}
          </>
        ) : b.return_photos_verified ? (
          <p data-testid={`return-photos-verified-${b.id}`} className="text-emerald-600">Photos verified ✓</p>
        ) : (
          <>
            <Button data-testid={`return-verify-${b.id}`} size="sm" variant="outline" className={btn} onClick={() => act(b.id, { return_photos_verified: true }, "Return photos verified")}>
              Verify photos
            </Button>
            <Button
              data-testid={`return-quick-close-${b.id}`}
              size="sm"
              variant="outline"
              disabled={quickClosing}
              className="rounded-full border-emerald-300 bg-transparent text-emerald-700 hover:bg-emerald-600 hover:text-white font-ui h-7 px-2.5 text-[11px] disabled:opacity-40"
              onClick={quickClose}
            >
              {quickClosing ? "Closing…" : "Quick Close ✓ verify + release + complete"}
            </Button>
            {(b.deposit_status || "held") === "held" && OUTSTANDING(b) > 0 && (
              <Button
                data-testid={`return-quick-close-deduct-${b.id}`}
                size="sm"
                variant="outline"
                disabled={quickClosing}
                className="rounded-full border-amber-300 bg-transparent text-amber-700 hover:bg-amber-600 hover:text-white font-ui h-7 px-2.5 text-[11px] disabled:opacity-40"
                onClick={() => quickCloseBooking(b, reload, setQuickClosing, true)}
              >
                {quickClosing ? "Closing…" : "Close + Deduct fees"}
              </Button>
            )}
          </>
        )}
        {b.gps_return?.lat && (
          <a data-testid={`return-gps-${b.id}`} href={`https://www.google.com/maps?q=${b.gps_return.lat},${b.gps_return.lng}`} target="_blank" rel="noreferrer" className="text-[#C71E2E] underline underline-offset-2">
            GPS ✓ {b.gps_return.accuracy_m ? `±${Math.round(b.gps_return.accuracy_m)}m` : "map"}
          </a>
        )}
        {(b.return_submitted || b.damage_reported_at) && (
          <Button data-testid={`return-compare-${b.id}`} size="sm" variant="outline" className={btn} onClick={() => setShowCompare(true)}>
            Compare photos
          </Button>
        )}
        {showCompare && <ComparePhotos bookingId={b.id} bookingRef={b.booking_ref} onClose={() => setShowCompare(false)} />}
        {b.status === "confirmed" && (
          <details data-testid={`manual-return-${b.id}`} className="border border-[#E5DED5] rounded-lg px-2.5 py-1.5">
            <summary className="cursor-pointer text-[#C71E2E] font-semibold select-none">Manual return — dropdowns, no photos needed</summary>
            <div className="mt-2 flex flex-col gap-1.5">
              <label className="flex items-center gap-1.5">
                <span className="w-14 shrink-0 text-muted-foreground">Fuel</span>
                <select data-testid={`manual-fuel-${b.id}`} value={mFuel} onChange={(e) => setMFuel(e.target.value)} className="input-dark h-7 text-xs rounded-md border border-[#E5DED5] px-1.5 bg-transparent flex-1">
                  <option>Full</option><option>3/4</option><option>1/2</option><option>1/4</option>
                </select>
              </label>
              <label className="flex items-center gap-1.5">
                <span className="w-14 shrink-0 text-muted-foreground">Keys</span>
                <select data-testid={`manual-keys-${b.id}`} value={mKeys} onChange={(e) => setMKeys(e.target.value)} className="input-dark h-7 text-xs rounded-md border border-[#E5DED5] px-1.5 bg-transparent flex-1">
                  <option value="returned">Keys returned</option>
                  <option value="left_in_vehicle">Keys left in vehicle</option>
                  <option value="missing">Keys missing</option>
                </select>
              </label>
              <label className="flex items-center gap-1.5">
                <span className="w-14 shrink-0 text-muted-foreground">Photos</span>
                <select data-testid={`manual-photos-${b.id}`} value={mPhotos} onChange={(e) => setMPhotos(e.target.value)} className="input-dark h-7 text-xs rounded-md border border-[#E5DED5] px-1.5 bg-transparent flex-1">
                  <option value="none">No photos available</option>
                  <option value="uploaded">Photos uploaded separately</option>
                </select>
              </label>
              <Button data-testid={`manual-close-${b.id}`} size="sm" variant="outline" disabled={mBusy} onClick={manualClose} className="rounded-full bg-[#1A1A1A] text-white hover:bg-black font-ui h-7 px-2.5 text-[11px] disabled:opacity-40">
                {mBusy ? "Closing…" : "Close rental (manual return)"}
              </Button>
            </div>
          </details>
        )}
        {(["pending_hold", "hold_failed", "hold_expired"].includes(depStatus) || (depStatus === "held" && b.deposit_hold_expires_at && new Date(b.deposit_hold_expires_at) < new Date())) && Number(b.deposit_amount) > 0 && (
          <Button data-testid={`return-deposit-hold-${b.id}`} size="sm" variant="outline" className={btn} onClick={() => api.post(`/admin/bookings/${b.id}/deposit-hold`).then(() => { toast.success(`Deposit hold placed — $${(Number(b.deposit_amount) || 150).toFixed(0)} held on guest's card`); reload(); }).catch((e) => toast.error(errMsg(e)))}>
            {depStatus === "held" ? "Re-hold" : "Hold"} ${(Number(b.deposit_amount) || 150).toFixed(0)} deposit
          </Button>
        )}
        {depStatus === "held" ? (
          <div className="flex gap-1.5">
            <Button data-testid={`return-deposit-release-${b.id}`} size="sm" variant="outline" className={btn} onClick={() => act(b.id, { deposit_action: "release" }, "Deposit hold released — guest is never charged")}>
              Release ${(Number(b.deposit_amount) || 150).toFixed(0)}
            </Button>
            <Button data-testid={`return-deposit-deduct-${b.id}`} size="sm" variant="outline" className={btn} onClick={() => {
              const out = Math.max(0, OUTSTANDING(b));
              const def = Math.min(Number(b.deposit_amount) || 150, out);
              const amt = window.prompt(`Outstanding penalty fees: $${out.toFixed(2)}. Amount to deduct from the $${(Number(b.deposit_amount) || 150).toFixed(0)} deposit on hold (the remainder is released):`, def.toFixed(2));
              if (amt === null) return;
              const note = window.prompt("Reason shown to the guest:", "Penalty fees deducted from security deposit");
              if (note === null) return;
              act(b.id, { deposit_action: "deduct", deposit_deduct_amount: parseFloat(amt), deposit_note: note }, "Penalty fees deducted from deposit — remainder released");
            }}>
              Deduct Penalties
            </Button>
            <Button data-testid={`return-deposit-charge-${b.id}`} size="sm" variant="outline" className={btn} onClick={() => { const note = window.prompt("Reason for charging the deposit (damage, fuel, late return, tolls):"); if (note === null) return; act(b.id, { deposit_action: "charge", deposit_note: note }, "Deposit charged"); }}>
              Charge ${(Number(b.deposit_amount) || 150).toFixed(0)}
            </Button>
          </div>
        ) : (
          <p data-testid={`return-deposit-status-${b.id}`} className={depStatus === "charged" ? "text-amber-600" : "text-emerald-600"}>
            Deposit {depStatus.replaceAll("_", " ")}{b.deposit_note ? ` — ${b.deposit_note}` : ""}
          </p>
        )}
      </div>
    </div>
  );
}

function TimesCell({ b, act }) {
  const [out, setOut] = useState(b.pickup_time || "");
  const [inT, setInT] = useState(b.dropoff_time || "");
  return (
    <div data-testid={`times-cell-${b.id}`}>
      <div className="flex items-center gap-1.5">
        <Input data-testid={`times-out-${b.id}`} value={out} onChange={(e) => setOut(e.target.value)} placeholder="Out: 10:00 AM" className="input-dark h-8 w-28 text-xs" />
        <Input data-testid={`times-in-${b.id}`} value={inT} onChange={(e) => setInT(e.target.value)} placeholder="In: 10:00 AM" className="input-dark h-8 w-28 text-xs" />
        <Button
          data-testid={`times-save-${b.id}`}
          size="sm"
          variant="outline"
          onClick={() => act(b.id, { pickup_time: out, dropoff_time: inT }, "In/out times saved — shown on contract & invoice")}
          className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] hover:text-[#C71E2E] font-ui h-8 px-3 text-xs"
        >
          Save
        </Button>
      </div>
      {(b.pickup_time || b.dropoff_time) && (
        <p data-testid={`times-saved-${b.id}`} className="text-xs text-emerald-600 font-ui mt-1">{b.pickup_time || "—"} → {b.dropoff_time || "—"}</p>
      )}
    </div>
  );
}

function WireProofPanel({ b }) {
  const [proofs, setProofs] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/bookings/${b.id}/wire-proofs`).then((r) => setProofs(r.data)).catch(() => setProofs([]));
  useEffect(() => { load(); }, [b.id]); // eslint-disable-line
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/bookings/${b.id}/wire-proof`, fd);
      toast.success("Wire proof attached");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`wire-proof-panel-${b.id}`} className="mt-3">
      <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1.5">Wire payment proof</p>
      <ul className="space-y-1 mb-2">
        {(proofs || []).map((p, i) => (
          <li key={p.id} data-testid={`wire-proof-item-${i}`} className="text-[11px] font-ui">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/wire-proofs/${p.id}/file`} target="_blank" rel="noreferrer" className="text-[#C71E2E] hover:underline">{p.original_filename || "proof"}</a>
            <span className="text-muted-foreground"> — {p.uploaded_by === "guest" ? "guest" : p.uploaded_by} · {String(p.created_at).slice(0, 16).replace("T", " ")}</span>
            {p.amount_status === "match" && (
              <span data-testid={`wire-proof-amount-${i}`} className="ml-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-px text-[9px] font-ui font-semibold text-emerald-700">matches ${Number(p.amount_detected).toFixed(2)}</span>
            )}
            {p.amount_status === "mismatch" && (
              <span data-testid={`wire-proof-amount-${i}`} className="ml-1.5 rounded-full border border-red-200 bg-red-50 px-1.5 py-px text-[9px] font-ui font-semibold text-red-700">shows ${Number(p.amount_detected).toFixed(2)} — expected ${Number(p.amount_expected).toFixed(2)}</span>
            )}
            {p.amount_status === "unreadable" && (
              <span data-testid={`wire-proof-amount-${i}`} className="ml-1.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[9px] font-ui font-semibold text-amber-700">verify amount manually</span>
            )}
          </li>
        ))}
        {proofs !== null && !proofs.length && <li className="text-[10px] font-ui text-muted-foreground">No proof uploaded yet.</li>}
      </ul>
      <label data-testid={`wire-proof-upload-label-${b.id}`} className={`inline-flex items-center rounded-full border border-[#C71E2E]/30 px-2.5 py-1 text-[10px] font-ui text-[#C71E2E] hover:bg-[#C71E2E]/10 cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
        <input data-testid={`wire-proof-upload-${b.id}`} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={upload} />
        {busy ? "Uploading…" : "+ Attach proof (photo/PDF)"}
      </label>
    </div>
  );
}

function EditDialogHistory({ b, onChanged, onUndone }) {
  const [bb, setBb] = useState(b);
  const [money, setMoney] = useState(null);
  const [undoBusy, setUndoBusy] = useState(false);
  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/bookings");
      const fresh = (data || []).find((x) => x.id === b.id);
      if (fresh) setBb(fresh);
    } catch {}
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}/financial-audit`);
      setMoney(data);
    } catch { setMoney([]); }
  };
  useEffect(() => {
    let live = true;
    (async () => { if (live) await refresh(); })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id]);
  const all = mergedHistory(bb, money);
  let lastDc = -1;
  all.forEach((h, i) => { if (h.dateChange) lastDc = i; });
  const undo = async () => {
    if (!window.confirm(`Undo the last date change for ${b.booking_ref}? Dates revert to the previous range (if still available), the price re-adjusts at the daily rate, and the guest is emailed/texted to re-sign the contract.`)) return;
    setUndoBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/undo-date-change`);
      toast.success(`Reverted to ${data.start_date} → ${data.end_date}${data.price_delta ? ` (${data.price_delta > 0 ? "+" : "−"}$${Math.abs(data.price_delta).toFixed(2)})` : ""} — guest asked to re-sign${data.times_restored === false ? " (times unchanged)" : ""}`);
      await refresh();
      if (onUndone) onUndone(data);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUndoBusy(false);
    }
  };
  return (
    <div data-testid="edit-dialog-history" className="mt-4">
      <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-2">
        Change history ({money === null ? `${historyOf(bb).length}+` : all.length})
      </p>
      <ul className="space-y-1 border-l-2 border-[#E5DED5] pl-3 max-h-36 overflow-y-auto pr-2">
        {all.map((h, i) => (
          <li key={i} data-testid={`edit-dialog-history-item-${i}`} className={`text-[10px] font-ui leading-snug ${h.money ? "text-[#8f1f2b]" : "text-[#4A4A4A]"}`}>
            <span className="text-muted-foreground">{String(h.at).slice(0, 16).replace("T", " ")}</span> — {h.money && <span className="font-semibold">$ </span>}{h.label}
            {i === lastDc && (
              <button data-testid="edit-dialog-history-undo" onClick={undo} disabled={undoBusy} className="ml-1.5 inline-flex items-center rounded-full border border-[#C71E2E]/30 px-1.5 py-px text-[9px] font-ui text-[#C71E2E] hover:bg-[#C71E2E]/10 disabled:opacity-50">
                <Undo2 size={9} className="mr-0.5" /> {undoBusy ? "Undoing…" : "Undo"}
              </button>
            )}
          </li>
        ))}
        {money !== null && !all.length && (
          <li className="text-[10px] font-ui text-muted-foreground">No history recorded yet.</li>
        )}
      </ul>
    </div>
  );
}

const DField = ({ label, children }) => (
  <div>
    <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">{label}</p>
    {children}
  </div>
);

function PaperSignDialog({ b, onClose, onSaved }) {
  const [name, setName] = useState(b.customer_name || "");
  const [lic, setLic] = useState("");
  const [busy, setBusy] = useState(false);
  const openPdf = async () => {
    try {
      const r = await api.get(`/admin/bookings/${b.id}/contract.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    if (!name.trim()) return toast.error("Signer name is required");
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${b.id}/mark-signed-paper`, { signer_name: name.trim(), license_number: lic.trim() });
      toast.success(`${b.booking_ref} marked signed (paper) — signed PDF emailed to the guest`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="paper-sign-dialog" className="max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Paper signature — {b.booking_ref}</DialogTitle></DialogHeader>
        <p className="text-[11px] font-ui text-muted-foreground mb-3">Print the contract, have the guest sign the paper copy, then record it here. The signed PDF is emailed to the guest.</p>
        <div className="space-y-3">
          <DField label="Signer name"><Input data-testid="paper-sign-name" value={name} onChange={(e) => setName(e.target.value)} className="input-dark" /></DField>
          <DField label="License number (optional)"><Input data-testid="paper-sign-license" value={lic} onChange={(e) => setLic(e.target.value)} className="input-dark" /></DField>
        </div>
        <Button data-testid="paper-sign-open-pdf" variant="outline" onClick={openPdf} className="rounded-full font-ui mt-3 w-full">Open contract PDF for printing</Button>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full font-ui">Cancel</Button>
          <Button data-testid="paper-sign-confirm" onClick={save} disabled={busy} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">{busy ? "Recording…" : "Mark as signed (paper)"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const reqsOf = (b) => {
  const items = [
    { ok: !!b.paid_at, label: "payment" },
    { ok: !!b.contract_signed_at, label: "contract" },
    { ok: !!b.license_uploaded, label: "license" },
    { ok: (b.renter_type || "local") === "local" ? !!(b.nib_number || "").trim() : !!(b.passport_number || "").trim(), label: (b.renter_type || "local") === "local" ? "NIB" : "passport" },
  ];
  if ((b.dropoff_location || "").toLowerCase().includes("airport")) items.push({ ok: !!(b.flight_number || "").trim(), label: "flight no." });
  return items;
};

function LicenseUploadButton({ b, onDone }) {  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  if (b.status === "cancelled") return null;
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "license_front");
    setBusy(true);
    try {
      await api.post(`/bookings/${b.id}/license`, fd);
      toast.success(`License attached to ${b.booking_ref}`);
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*,.pdf" className="hidden" onChange={onFile} data-testid={`booking-license-input-${b.id}`} />
      <button data-testid={`booking-upload-license-${b.id}`} onClick={() => ref.current?.click()} className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">
        {busy ? "Uploading…" : b.license_uploaded ? "License ✓" : "License"}
      </button>
    </>
  );
}

function ManualBookingDialog({ onClose, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [f, setF] = useState({
    vehicle_id: "", start_date: "", end_date: "", customer_name: "", customer_email: "", customer_phone: "",
    pickup_location: "West Bay Street office", dropoff_location: "Same as pickup",
    pickup_time: "", dropoff_time: "", payment_method: "cash", mark_paid: false, send_notifications: true,
    price_override: "", waive_deposit: false, vat_exempt: false, walk_in: false, quote_only: false, coupon_code: "", notes: "",
    license_number: "", passport_number: "", nib_number: "", renter_type: "local", flight_number: "", sli: false,
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/vehicles").then((r) => setVehicles((r.data || []).filter((v) => v.active !== false))).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    if (!f.vehicle_id || !f.start_date || !f.end_date || !f.customer_name.trim()) return toast.error("Vehicle, dates and guest name are required");
    setBusy(true);
    const payload = { ...f, price_override: f.price_override ? Number(f.price_override) : null };
    try {
      if (f.quote_only) {
        if (!f.customer_email.trim() && !f.customer_phone.trim()) { toast.error("Quote needs a guest email or phone"); return; }
        const { data } = await api.post("/admin/quotes", payload);
        toast.success(`Quote ${data.quote.quote_ref} ${f.send_notifications ? "sent to the guest" : "saved (not sent)"}`);
        onSaved();
        return;
      }
      const { data } = await api.post("/admin/bookings/manual", payload);
      toast.success(`Booking ${data.booking.booking_ref} created${f.mark_paid ? " & marked paid" : ""}`);
      onSaved();
    } catch (e) {
      const msg = errMsg(e);
      if (msg.toLowerCase().includes("walk-in") && window.confirm(`${msg}\n\nOverride and create anyway?`)) {
        try {
          const { data } = await api.post("/admin/bookings/manual", { ...payload, force: true });
          toast.success(`Booking ${data.booking.booking_ref} created (walk-in override)`);
          onSaved();
          return;
        } catch (e2) { toast.error(errMsg(e2)); }
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="manual-booking-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display tracking-tight">New manual booking</DialogTitle></DialogHeader>
        <p className="text-[11px] font-ui text-muted-foreground mb-3">Walk-ins & phone bookings — creates the rental, blocks the dates, and optionally confirms payment.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Vehicle</p>
            <select data-testid="manual-vehicle" value={f.vehicle_id} onChange={set("vehicle_id")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">
              <option value="">Select vehicle…</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} — ${v.price_per_day}/day</option>)}
            </select>
          </div>
          <DField label="Pickup date"><Input data-testid="manual-start" type="date" value={f.start_date} onChange={set("start_date")} className="input-dark" /></DField>
          <DField label="Return date"><Input data-testid="manual-end" type="date" value={f.end_date} onChange={set("end_date")} className="input-dark" /></DField>
          <DField label="Guest name"><Input data-testid="manual-name" value={f.customer_name} onChange={set("customer_name")} className="input-dark" /></DField>
          <DField label="Guest email (optional)"><Input data-testid="manual-email" value={f.customer_email} onChange={set("customer_email")} className="input-dark" /></DField>
          <DField label="Guest phone (optional)"><Input data-testid="manual-phone" value={f.customer_phone} onChange={set("customer_phone")} className="input-dark" /></DField>
          <DField label="Driver's license no. (optional)"><Input data-testid="manual-license-number" value={f.license_number} onChange={set("license_number")} className="input-dark" /></DField>
          <DField label="Passport no. (optional)"><Input data-testid="manual-passport-number" value={f.passport_number} onChange={set("passport_number")} className="input-dark" /></DField>
          <DField label="NIB no. — Bahamas (optional)"><Input data-testid="manual-nib-number" value={f.nib_number} onChange={set("nib_number")} className="input-dark" /></DField>
          <div>
            <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Guest type</p>
            <select data-testid="manual-renter-type" value={f.renter_type} onChange={set("renter_type")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">
              <option value="local">Local resident (NIB)</option>
              <option value="international">International visitor (passport)</option>
            </select>
            {f.renter_type === "international" && <p data-testid="manual-renter-type-note" className="mt-1 text-xs text-sky-700 font-ui">Guest will be asked to upload their passport photo</p>}
          </div>
          <DField label="Flight no. & airline (optional)"><Input data-testid="manual-flight-number" value={f.flight_number} onChange={set("flight_number")} placeholder="e.g. AA 1234 — American" className="input-dark" /></DField>
          <label className="flex items-start gap-2.5 rounded-xl border border-border px-4 py-3 cursor-pointer">
            <input type="checkbox" data-testid="manual-sli" checked={!!f.sli} onChange={set("sli")} className="mt-0.5 h-4 w-4 accent-[#C71E2E]" />
            <span>
              <span className="text-sm font-ui font-medium block">Add SLI — Supplementary Liability Insurance</span>
              <span className="text-xs text-muted-foreground">$7.99/day · $500 deductible · added to the rental total</span>
            </span>
          </label>
          <DField label="Total override ($ — blank = auto price)"><Input data-testid="manual-price" type="number" min="0" value={f.price_override} onChange={set("price_override")} placeholder="auto" className="input-dark" /></DField>
          <DField label="Coupon code (optional)"><Input data-testid="manual-coupon" value={f.coupon_code} onChange={set("coupon_code")} placeholder="BUDDYPASS" className="input-dark uppercase" /></DField>
          <DField label="Pickup time"><Input data-testid="manual-pickup-time" value={f.pickup_time} onChange={set("pickup_time")} placeholder="10:00 AM" className="input-dark" /></DField>
          <DField label="Return time"><Input data-testid="manual-dropoff-time" value={f.dropoff_time} onChange={set("dropoff_time")} placeholder="10:00 AM" className="input-dark" /></DField>
          <div>
            <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Pick-up location</p>
            <select data-testid="manual-pickup-location" value={f.pickup_location} onChange={set("pickup_location")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">
              <option>West Bay Street office</option>
              <option>Lynden Pindling Intl Airport (NAS)</option>
              <option>Downtown Nassau</option>
              <option>Cable Beach</option>
              <option>Paradise Island</option>
              <option>Prince George Wharf (Cruise Port)</option>
              <option>Other / villa / hotel (custom)</option>
            </select>
          </div>
          <div>
            <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Drop-off location</p>
            <select data-testid="manual-dropoff-location" value={f.dropoff_location} onChange={set("dropoff_location")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">
              <option>Same as pickup</option>
              <option>West Bay Street office</option>
              <option>Lynden Pindling Intl Airport (NAS)</option>
              <option>Downtown Nassau</option>
              <option>Cable Beach</option>
              <option>Paradise Island</option>
              <option>Prince George Wharf (Cruise Port)</option>
              <option>Other / villa / hotel (custom)</option>
            </select>
            {f.dropoff_location.includes("Airport") && <p data-testid="manual-airport-dropoff-notice" className="mt-1 text-xs text-amber-600 font-ui">Airport drop-off adds a $25 fee to the total</p>}
          </div>
          <div>
            <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Payment method</p>
            <select data-testid="manual-method" value={f.payment_method} onChange={set("payment_method")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">
              <option value="cash">Cash</option>
              <option value="wire">Bank wire</option>
              <option value="zelle">Zelle</option>
              <option value="manual_card">Card (in person)</option>
              <option value="card">Card (online link)</option>
              <option value="paypal">PayPal (online link)</option>
            </select>
          </div>
          <DField label="Notes (optional)"><Input data-testid="manual-notes" value={f.notes} onChange={set("notes")} className="input-dark" /></DField>
        </div>
        <div className="mt-4 space-y-2">
          <label className="flex items-center gap-2 text-sm font-ui"><input data-testid="manual-mark-paid" type="checkbox" checked={f.mark_paid} onChange={set("mark_paid")} /> Payment collected — mark as paid & confirm now</label>
          <label className="flex items-center gap-2 text-sm font-ui"><input data-testid="manual-waive-deposit" type="checkbox" checked={f.waive_deposit} onChange={set("waive_deposit")} /> Waive $150 security deposit</label>
          <label className="flex items-center gap-2 text-sm font-ui"><input data-testid="manual-vat-exempt" type="checkbox" checked={f.vat_exempt} onChange={set("vat_exempt")} /> VAT exempt — do not charge 10% VAT (VAT is ON by default)</label>
          <label className="flex items-center gap-2 text-sm font-ui"><input data-testid="manual-walk-in" type="checkbox" checked={f.walk_in} onChange={set("walk_in")} /> Walk-in — customer is present (license upload + contract signature required before marking paid)</label>
          <label className="flex items-center gap-2 text-sm font-ui"><input data-testid="manual-quote-only" type="checkbox" checked={f.quote_only} onChange={set("quote_only")} /> Send as quote only — no booking created; guest gets an accept &amp; pay link (valid 7 days)</label>
          <label className="flex items-center gap-2 text-sm font-ui"><input data-testid="manual-notify" type="checkbox" checked={f.send_notifications} onChange={set("send_notifications")} /> Email/SMS the guest (confirmation or payment link)</label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button data-testid="manual-cancel" variant="outline" onClick={onClose} className="rounded-full font-ui">Cancel</Button>
          <Button data-testid="manual-save" onClick={save} disabled={busy} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">{busy ? "Creating…" : f.mark_paid ? "Create & confirm" : "Create booking"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditBookingDialog({ b, onClose, onSaved, onChanged }) {
  const [f, setF] = useState({
    customer_name: b.customer_name || "", customer_email: b.customer_email || "", customer_phone: b.customer_phone || "",
    pickup_location: b.pickup_location || "", dropoff_location: b.dropoff_location || "", fuel_level: b.fuel_level || "Full",
    start_date: b.start_date || "", end_date: b.end_date || "", pickup_time: b.pickup_time || "", dropoff_time: b.dropoff_time || "",
    payment_method: b.payment_method || "card",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const Field = ({ label, k, type = "text", placeholder = "" }) => (
    <div>
      <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">{label}</p>
      <Input data-testid={`edit-${k}`} type={type} value={f[k]} onChange={set(k)} placeholder={placeholder} className="input-dark" />
    </div>
  );
  const save = async () => {
    setBusy(true);
    try {
      if (f.start_date !== b.start_date || f.end_date !== b.end_date || f.pickup_time !== (b.pickup_time || "") || f.dropoff_time !== (b.dropoff_time || "")) {
        await api.post(`/admin/bookings/${b.id}/change-dates`, { start_date: f.start_date, end_date: f.end_date, pickup_time: f.pickup_time, dropoff_time: f.dropoff_time });
      }
      const patch = {};
      ["customer_name", "customer_email", "customer_phone", "pickup_location", "dropoff_location", "fuel_level"].forEach((k) => { if (f[k] !== (b[k] ?? "")) patch[k] = f[k]; });
      if (b.status === "pending_payment" && f.payment_method !== (b.payment_method || "card")) patch.payment_method = f.payment_method;
      if (Object.keys(patch).length) await api.patch(`/admin/bookings/${b.id}`, patch);
      toast.success(`Booking ${b.booking_ref} updated${patch.customer_email ? " — guest email changed, resend them their links" : ""}${patch.payment_method ? ` — guest emailed/texted ${METHOD_LABELS[f.payment_method]} instructions` : ""}`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="booking-edit-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Edit booking {b.booking_ref}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <Field label="Guest name" k="customer_name" />
          <Field label="Guest email" k="customer_email" type="email" />
          <Field label="Guest phone" k="customer_phone" />
          <div>
            <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Fuel at pickup</p>
            <select data-testid="edit-fuel_level" value={f.fuel_level} onChange={set("fuel_level")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">
              {["Full", "3/4", "1/2", "1/4"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          {b.status === "pending_payment" && (
            <div>
              <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-muted-foreground mb-1">Payment method</p>
              <select data-testid="edit-payment_method" value={f.payment_method} onChange={set("payment_method")} className="input-dark w-full h-10 rounded-xl px-3 text-sm font-ui">
                <option value="card">Card (Stripe)</option>
                <option value="paypal">PayPal</option>
                <option value="zelle">Zelle</option>
                <option value="wire">Bank wire transfer</option>
                <option value="manual_card">Card (manual)</option>
              </select>
            </div>
          )}
          <Field label="Pickup location" k="pickup_location" />
          <Field label="Drop-off location" k="dropoff_location" />
          <Field label="Pickup date" k="start_date" type="date" />
          <Field label="Return date" k="end_date" type="date" />
          <Field label="Pickup time" k="pickup_time" placeholder="10:00 AM" />
          <Field label="Return time" k="dropoff_time" placeholder="10:00 AM" />
        </div>
        <p className="text-[11px] font-ui text-muted-foreground mt-2 leading-relaxed">Date or time changes re-check availability, adjust the price at the daily rate, and the guest is asked to re-sign the contract. Guest detail changes update instantly.</p>
        <EditDialogHistory b={b} onChanged={onChanged} onUndone={(d) => setF((prev) => ({ ...prev, start_date: d.start_date, end_date: d.end_date, ...(d.pickup_time && d.pickup_time !== "TBD" ? { pickup_time: d.pickup_time } : {}), ...(d.dropoff_time && d.dropoff_time !== "TBD" ? { dropoff_time: d.dropoff_time } : {}) }))} />
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui">Cancel</Button>
          <Button data-testid="booking-edit-save" onClick={save} disabled={busy} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">{busy ? "Saving…" : "Save changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BookingsTab({ focusBooking = "" }) {
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");

  const filtered = bookings.filter((b) => {
    const q = search.trim().toLowerCase();
    return !q || [b.booking_ref, b.customer_name, b.customer_email, b.vehicle_name].some((v) => (v || "").toLowerCase().includes(q));
  });

  const knownIds = useRef(null);
  const chime = (freqs = [880, 1174.66]) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      freqs.forEach((f, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = f;
        o.type = "sine";
        g.gain.setValueAtTime(0.08, ctx.currentTime + i * 0.18);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.25);
        o.connect(g).connect(ctx.destination);
        o.start(ctx.currentTime + i * 0.18);
        o.stop(ctx.currentTime + i * 0.18 + 0.3);
      });
      setTimeout(() => ctx.close(), 1200);
    } catch (e) {}
  };
  const [editFor, setEditFor] = useState(null);
  const [paperFor, setPaperFor] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const load = () => api.get("/admin/bookings").then((r) => {
    const fresh = r.data;
    if (knownIds.current !== null) {
      const newOnes = fresh.filter((b) => !knownIds.current.has(b.id));
      newOnes.forEach((b) => toast(`New booking ${b.booking_ref} — $${Number(b.total || 0).toFixed(2)}, ${b.vehicle_name}`, { icon: <Bell size={15} className="text-[#C71E2E]" /> }));
      if (newOnes.length) chime();
      const justPaid = fresh.filter((b) => knownIds.current.get(b.id) === "pending_payment" && b.status === "confirmed");
      justPaid.forEach((b) => toast(`Payment received — ${b.booking_ref} confirmed ($${Number(b.total || 0).toFixed(2)}, ${b.vehicle_name})`, { icon: <DollarSign size={15} className="text-emerald-600" /> }));
      if (justPaid.length) chime([1318.5, 1760, 2093]);
    }
    knownIds.current = new Map(fresh.map((b) => [b.id, b.status]));
    setBookings(fresh);
  }).catch(() => {});
  useEffect(() => { load(); api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) load(); }, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const capOf = (vid) => vehicles.find((v) => v.id === vid)?.tank_capacity || 0;

  const act = async (id, payload, msg) => {
    try {
      await api.patch(`/admin/bookings/${id}`, payload);
      toast.success(msg);
      load();
    } catch (e) {
      const em = errMsg(e);
      if (em.toLowerCase().includes("walk-in") && payload.mark_paid && !payload.force && window.confirm(`${em}\n\nOverride and confirm anyway?`)) {
        try {
          await api.patch(`/admin/bookings/${id}`, { ...payload, force: true });
          toast.success(`${msg} (walk-in override)`);
          load();
        } catch (e2) { toast.error(errMsg(e2)); }
      } else {
        toast.error(em);
      }
    }
  };

  const [changeFor, setChangeFor] = useState(null);
  const [changeVehicleId, setChangeVehicleId] = useState("");
  const [changeFree, setChangeFree] = useState(true);
  const [openId, setOpenId] = useState(focusBooking || null);

  const changeVehicle = async (id) => {
    const v = vehicles.find((x) => x.id === changeVehicleId);
    if (!v) return;
    const label = v.name || `${v.year} ${v.make} ${v.model}`;
    if (!window.confirm(changeFree
      ? `Free upgrade to ${label}? Price stays the same and the customer must re-sign the contract.`
      : `Change to ${label}? Price adjusts by the daily-rate difference and the customer must re-sign the contract.`)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${id}/change-vehicle`, { vehicle_id: changeVehicleId, free: changeFree });
      toast.success(`Vehicle changed to ${data.vehicle_name} (${data.mode}${data.price_delta ? `, ${data.price_delta > 0 ? "+" : "−"}$${Math.abs(data.price_delta).toFixed(2)}` : ""}) — new contract sent for re-signing`);
      setChangeFor(null); setChangeVehicleId(""); setChangeFree(true);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-bookings-tab">
      <AdminHeader testid="admin-header-bookings" eyebrow="Operations" title="Bookings" description="Confirm payments, add tolls and manage rentals." actions={<div className="flex flex-col sm:flex-row gap-2 sm:items-center"><Button data-testid="manual-booking-open" onClick={() => setShowManual(true)} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui h-10 px-5 shrink-0"><Plus size={14} className="mr-1.5" /> New booking</Button><div className="w-full sm:w-72"><Input data-testid="bookings-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer, email, ref or vehicle…" className="input-dark" /></div></div>} />
      <TodaysReturns bookings={bookings} reload={load} />
      <TodaysPickups bookings={bookings} />
      {editFor && <EditBookingDialog b={editFor} onClose={() => setEditFor(null)} onSaved={() => { setEditFor(null); load(); }} onChanged={load} />}
      {showManual && <ManualBookingDialog onClose={() => setShowManual(false)} onSaved={() => { setShowManual(false); load(); }} />}
      {paperFor && <PaperSignDialog b={paperFor} onClose={() => setPaperFor(null)} onSaved={() => { setPaperFor(null); load(); }} />}
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#E5DED5] hover:bg-transparent">
            <TableHead>Booking & Guest</TableHead>
            <TableHead>Vehicle & Schedule</TableHead>
            <TableHead>Financials</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Flags</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.length === 0 && (
            <TableRow className={ROW}>
              <TableCell colSpan={6} className="text-muted-foreground font-ui py-10 text-center">No bookings yet.</TableCell>
            </TableRow>
          )}
          {filtered.map((b) => {
            const open = openId === b.id;
            const out = Math.max(0, OUTSTANDING(b));
            return (
            <Fragment key={b.id}>
            <TableRow data-testid={`admin-booking-row-${b.id}`} onClick={() => setOpenId(open ? null : b.id)} className={`${ROW} cursor-pointer ${focusBooking === b.id ? "bg-amber-50 ring-2 ring-inset ring-[#C71E2E]/40" : ""}`}>
              <TableCell>
                <p className="text-[#C71E2E] font-ui font-medium whitespace-nowrap">{b.booking_ref}</p>
                <p className="font-ui text-sm font-medium">{b.customer_name}</p>
                <p className="text-xs text-muted-foreground">{b.customer_email}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {b.docs_approved && (
                    <span data-testid={`booking-verified-${b.id}`} className="inline-flex items-center gap-1 text-[10px] font-ui font-semibold tracking-[0.12em] uppercase text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-full px-2 py-0.5">
                      ✓ Verified Renter
                    </span>
                  )}
                  {b.fraud_flagged && (
                    <span data-testid={`booking-fraud-flag-${b.id}`} className="inline-flex items-center gap-1 text-[10px] font-ui font-semibold tracking-[0.12em] uppercase text-red-700 border border-red-300 bg-red-50 rounded-full px-2 py-0.5">
                      ⚠ Fraud Flagged
                    </span>
                  )}
                  {b.contract_resign_pending && !b.contract_signed_at && (
                    <span data-testid={`booking-resign-pending-${b.id}`} className="inline-flex items-center gap-1 text-[10px] font-ui font-semibold tracking-[0.12em] uppercase text-amber-700 border border-amber-300 bg-amber-50 rounded-full px-2 py-0.5">
                      Awaiting re-signature
                    </span>
                  )}
                  {b.contract_re_signed_at && (
                    <span data-testid={`booking-resigned-${b.id}`} className="text-[10px] font-ui font-semibold tracking-[0.08em] uppercase text-emerald-700">
                      Re-signed {new Date(b.contract_re_signed_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <p className="whitespace-nowrap text-sm">{b.vehicle_name}</p>
                <p className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</p>
                {(b.pickup_time || b.dropoff_time) && (
                  <p className="text-xs text-muted-foreground mt-0.5">{b.pickup_time || "—"} out → {b.dropoff_time || "—"} in</p>
                )}
              </TableCell>
              <TableCell>
                <p className="font-ui text-sm font-semibold">{fmtMoney(b.total)}</p>
                {out > 0 && <p className="text-xs font-ui font-medium text-amber-700 mt-0.5">+{fmtMoney(out)} fees outstanding</p>}
                <p className="text-xs text-muted-foreground capitalize mt-0.5">{b.payment_method || "—"}</p>
                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                  <select
                    data-testid={`booking-paystatus-${b.id}`}
                    value={payStatus(b)}
                    onChange={async (e) => {
                      const to = e.target.value;
                      const cur = payStatus(b);
                      if (to === cur) return;
                      const notes = {
                        paid: "If it was awaiting payment, the guest is emailed/texted a confirmation.",
                        awaiting: "The booking returns to Awaiting payment so the guest can pay again via any method. Deposit and contract state are untouched.",
                        refunded: "The rental total is logged as refunded in Money activity for reconciliation — issue the actual refund through Stripe/PayPal/your bank.",
                      };
                      if (!window.confirm(`Change ${b.booking_ref} payment status to "${PAY_STATUS_LABELS[to]}"? ${notes[to]}`)) { e.target.value = cur; return; }
                      try {
                        await api.post(`/admin/bookings/${b.id}/payment-status`, { status: to });
                        toast.success(`Payment status → ${PAY_STATUS_LABELS[to]}`);
                        load();
                      } catch (err) {
                        const msg = errMsg(err);
                        if (msg.toLowerCase().includes("walk-in") && window.confirm(`${msg}\n\nOverride and mark paid anyway?`)) {
                          try {
                            await api.post(`/admin/bookings/${b.id}/payment-status`, { status: to, force: true });
                            toast.success(`Payment status → ${PAY_STATUS_LABELS[to]} (walk-in override)`);
                            load();
                            return;
                          } catch (err2) { toast.error(errMsg(err2)); }
                        } else { toast.error(msg); }
                        e.target.value = cur;
                      }
                    }}
                    className="h-7 rounded-full border border-[#E5DED5] bg-white px-2 text-[10px] font-ui text-foreground/80"
                  >
                    <option value="paid">Paid</option>
                    <option value="awaiting">Awaiting payment</option>
                    <option value="refunded">Refunded</option>
                  </select>
                  {b.payment_state === "refunded" && (
                    <span data-testid={`booking-refunded-${b.id}`} className="border rounded-full px-2 py-0.5 text-[9px] font-ui font-semibold uppercase tracking-wide text-red-700 border-red-200 bg-red-50">refunded</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <span data-testid={`admin-booking-status-${b.id}`} className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 whitespace-nowrap ${STATUS_STYLES[b.status] || ""}`}>
                  {b.status.replace("_", " ")}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex flex-col items-start gap-1">
                  <span className={`border rounded-full px-2 py-0.5 text-[10px] font-ui whitespace-nowrap ${(b.deposit_status || "held") === "held" ? "text-amber-700 border-amber-200 bg-amber-50" : "text-emerald-700 border-emerald-200 bg-emerald-50"}`}>deposit {(b.deposit_status || "held").replaceAll("_", " ")}</span>
                  {b.picked_up_at && (
                    <span data-testid={`booking-picked-up-chip-${b.id}`} className="border rounded-full px-2 py-0.5 text-[10px] font-ui whitespace-nowrap text-emerald-700 border-emerald-200 bg-emerald-50">picked up ✓</span>
                  )}
                  <span className={`border rounded-full px-2 py-0.5 text-[10px] font-ui whitespace-nowrap ${b.contract_signed_at ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-muted-foreground border-[#E5DED5]"}`}>{b.contract_signed_at ? "contract ✓" : "contract pending"}</span>
                  {b.walk_in && (
                    <span data-testid={`booking-walkin-${b.id}`} className={`border rounded-full px-2 py-0.5 text-[10px] font-ui whitespace-nowrap ${b.license_uploaded && b.contract_signed_at ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-amber-800 border-amber-300 bg-amber-50"}`}>
                      {b.license_uploaded && b.contract_signed_at ? "walk-in complete ✓" : `walk-in — missing: ${[!b.license_uploaded && "license", !b.contract_signed_at && "contract"].filter(Boolean).join(" + ")}`}
                    </span>
                  )}
                  {b.status !== "cancelled" && (() => {
                    const items = reqsOf(b);
                    const done = items.filter((i) => i.ok).length;
                    const missing = items.filter((i) => !i.ok).map((i) => i.label).join(", ");
                    return (
                      <span data-testid={`booking-reqs-${b.id}`} title={done === items.length ? "All requirements complete" : `Missing: ${missing}`} className={`border rounded-full px-2 py-0.5 text-[10px] font-ui whitespace-nowrap cursor-help ${done === items.length ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-amber-800 border-amber-300 bg-amber-50"}`}>
                        {done === items.length ? "ready ✓" : `${done}/${items.length} ready`}
                      </span>
                    );
                  })()}
                  {(b.status === "confirmed" || b.status === "completed") && (
                    <span className={`border rounded-full px-2 py-0.5 text-[10px] font-ui whitespace-nowrap ${b.return_photos_verified ? "text-emerald-700 border-emerald-200 bg-emerald-50" : b.return_submitted ? "text-amber-700 border-amber-200 bg-amber-50" : "text-muted-foreground border-[#E5DED5]"}`}>{b.return_photos_verified ? "photos verified ✓" : b.return_submitted ? "photos to verify" : "return photos awaiting"}</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                {b.status === "pending_payment" && b.payment_method === "wire" && (
                  <Button data-testid={`booking-wire-received-${b.id}`} size="sm" onClick={() => { if (!window.confirm(`Confirm the bank wire for ${b.booking_ref} (${fmtMoney(b.total)}) has arrived? The booking is confirmed and the guest gets a payment receipt by email + SMS.`)) return; act(b.id, { mark_paid: true }, `Wire received — ${b.booking_ref} confirmed, receipt sent to the guest`); }} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-ui mr-2">
                    <Landmark size={12} className="mr-1" /> Wire received
                  </Button>
                )}
                {b.status === "pending_payment" && (
                  <span className="inline-flex items-center gap-1.5 mr-2">
                    <select id={`paymethod-${b.id}`} data-testid={`booking-paymethod-${b.id}`} defaultValue={b.payment_method || "card"} className="border border-[#E5DED5] rounded-full px-2 py-1 text-xs font-ui bg-white h-7">
                      <option value="card">Card (Stripe)</option>
                      <option value="paypal">PayPal</option>
                      <option value="zelle">Zelle</option>
                      <option value="wire">Bank wire</option>
                      <option value="manual_card">Card (manual)</option>
                    </select>
                    <Button data-testid={`booking-mark-paid-${b.id}`} size="sm" onClick={() => { const m = (document.getElementById(`paymethod-${b.id}`) || {}).value || b.payment_method || "manual"; act(b.id, { payment_method: m, mark_paid: true }, `Payment confirmed via ${{ card: "Card (Stripe)", paypal: "PayPal", zelle: "Zelle", wire: "Bank wire", manual_card: "Card (manual)" }[m] || m} — booking confirmed & email sent`); }} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">
                      Confirm Payment
                    </Button>
                  </span>
                )}
                {b.status !== "cancelled" && !b.contract_signed_at && (b.customer_email || b.customer_phone) && (
                  <button data-testid={`booking-resend-contract-${b.id}`} onClick={async () => { try { await api.post(`/admin/bookings/${b.id}/resend-contract`); toast.success(`Contract signing link re-sent to ${b.customer_name} (email${b.customer_phone ? " + SMS" : ""})`); } catch (err) { toast.error(errMsg(err)); } }} className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">
                    Contract ↻
                  </button>
                )}
                {b.status !== "cancelled" && (!b.contract_signed_at || b.contract_resign_pending) && (
                  <button data-testid={`booking-paper-sign-${b.id}`} onClick={() => setPaperFor(b)} className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">
                    Paper sign
                  </button>
                )}
                <LicenseUploadButton b={b} onDone={load} />
                <Link data-testid={`booking-receipt-${b.id}`} to={`/receipt/${b.id}`} className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">
                  Invoice
                </Link>
                <button data-testid={`booking-print-${b.id}`} onClick={() => window.open(`/receipt/${b.id}?print=1`, "_blank")} className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">
                  Print
                </button>
                <Button data-testid={`booking-manage-${b.id}`} size="sm" variant="outline" onClick={() => setOpenId(open ? null : b.id)} className="rounded-full border-[#C71E2E]/40 bg-transparent text-[#C71E2E] hover:bg-[#C71E2E] hover:text-white font-ui h-8 px-3 text-xs">
                  {open ? "Close ▲" : "Manage ▼"}
                </Button>
              </TableCell>
            </TableRow>
            {open && (
              <TableRow className="border-[#E5DED5] hover:bg-transparent">
                <TableCell colSpan={6} className="bg-[#FAF7F2] p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" onClick={(e) => e.stopPropagation()}>
                    <section className="bg-white border border-[#E5DED5] rounded-xl p-4">
                      <p className="font-ui text-xs font-semibold tracking-[0.2em] uppercase text-[#4A4A4A] mb-3">Tolls & In/Out Times</p>
                      <div className="space-y-4">
                        <div>
                          <p className="text-[10px] font-ui uppercase tracking-wide text-muted-foreground mb-1.5">Paradise Island bridge toll — enter trips for the $8.99 toll pass + $3.00/trip, or set a manual amount</p>
                          <TollCell b={b} act={act} />
                        </div>
                        <div>
                          <p className="text-[10px] font-ui uppercase tracking-wide text-muted-foreground mb-1.5">Pickup & return times — shown on contract & invoice</p>
                          <TimesCell b={b} act={act} />
                        </div>
                      </div>
                    </section>
                    <section className="bg-white border border-[#E5DED5] rounded-xl p-4">
                      <p className="font-ui text-xs font-semibold tracking-[0.2em] uppercase text-[#4A4A4A] mb-3">Fees & Penalties</p>
                      <PenaltiesCell b={b} act={act} cap={capOf(b.vehicle_id)} />
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {(b.status === "confirmed" || b.status === "completed") && out > 0 && (
                          <Button data-testid={`booking-charge-penalty-${b.id}`} size="sm" variant="outline" onClick={async () => {
                            const amt = window.prompt(`Outstanding charges: $${out.toFixed(2)}${b.stripe_card_last4 ? ` — card on file: ${(b.stripe_card_brand || "card").toUpperCase()} •••• ${b.stripe_card_last4}` : " — NO card on file (guest pays via payment link instead)"}. Amount to charge now:`, out.toFixed(2));
                            if (amt === null) return;
                            const reason = window.prompt("Reason for the charge (shown to the guest):", "Post-rental charges");
                            if (reason === null) return;
                            try {
                              const { data } = await api.post(`/admin/bookings/${b.id}/charge-penalty`, { amount: parseFloat(amt), reason });
                              toast.success(`Charged $${data.amount} to ${data.card}`);
                              load();
                            } catch (e) { toast.error(errMsg(e)); }
                          }} className="rounded-full border-amber-300 bg-transparent text-amber-700 hover:bg-amber-50 font-ui h-7 px-2.5 text-[11px]">
                            Charge Penalty
                          </Button>
                        )}
                        {(b.penalty_charges || []).some((c) => !c.reversed) && (() => {
                          const last = [...(b.penalty_charges || [])].reverse().find((c) => !c.reversed);
                          return (
                            <Button data-testid={`booking-undo-penalty-${b.id}`} size="sm" variant="outline" onClick={async () => {
                              if (!window.confirm(`Refund the $${Number(last.amount).toFixed(2)} penalty charge (${last.reason || "post-rental charges"}) back to the guest's card? The outstanding balance will be restored.`)) return;
                              try { const { data } = await api.post(`/admin/bookings/${b.id}/undo-penalty`, {}); toast.success(`Refunded $${Number(data.refunded).toFixed(2)} — outstanding balance restored`); load(); }
                              catch (e) { toast.error(errMsg(e)); }
                            }} className="rounded-full border-sky-300 bg-transparent text-sky-700 hover:bg-sky-50 font-ui h-7 px-2.5 text-[11px]">
                              Undo Penalty
                            </Button>
                          );
                        })()}
                        {out > 0 && (
                          <Button data-testid={`booking-remove-penalties-${b.id}`} size="sm" variant="outline" onClick={() => { if (!window.confirm(`Remove ALL ${fmtMoney(out)} of outstanding penalty charges from this invoice? The guest will not be charged. If money was already collected, use Undo Penalty (refund) instead.`)) return; act(b.id, { waive_penalties: true }, "Penalty charges removed from invoice"); }} className="rounded-full border-red-300 bg-transparent text-red-700 hover:bg-red-50 font-ui h-7 px-2.5 text-[11px]">
                            Remove Penalties
                          </Button>
                        )}
                      </div>
                      {out > 0 && <p className="mt-2 text-xs font-ui text-amber-700">{fmtMoney(out)} outstanding — charge the card on file or deduct from the deposit</p>}
                    </section>
                    <section className="bg-white border border-[#E5DED5] rounded-xl p-4">
                      <p className="font-ui text-xs font-semibold tracking-[0.2em] uppercase text-[#4A4A4A] mb-3">Return Checklist & Deposit</p>
                      <ReturnCell b={b} act={act} cap={capOf(b.vehicle_id)} focus={focusBooking === b.id} reload={load} />
                    </section>
                    <section className="bg-white border border-[#E5DED5] rounded-xl p-4">
                      <p className="font-ui text-xs font-semibold tracking-[0.2em] uppercase text-[#4A4A4A] mb-3">Payment Card & Vehicle</p>
                      <div className="flex flex-wrap items-center gap-2">
                        {b.stripe_card_pm ? (
                          <span data-testid={`booking-card-on-file-${b.id}`} className="text-[10px] font-ui font-semibold uppercase tracking-wide text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-full px-2 py-0.5">{(b.stripe_card_brand || "Card")} •••• {b.stripe_card_last4 || ""} on file</span>
                        ) : b.status !== "cancelled" && (
                          <Button data-testid={`booking-hold-card-${b.id}`} size="sm" variant="outline" onClick={async () => {
                            try {
                              const { data } = await api.post(`/bookings/${b.id}/save-card`, { origin_url: window.location.origin });
                              const msg = `Easy Drive Car Rental — please save your card on file for booking ${b.booking_ref} (used only for post-rental charges per your rental agreement): ${data.checkout_url}`;
                              if (b.customer_phone) window.open(`https://wa.me/${String(b.customer_phone).replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`, "_blank");
                              try { await navigator.clipboard.writeText(data.checkout_url); } catch (e2) {}
                              toast.success("Card-hold link created — opened WhatsApp & copied the link");
                            } catch (e) { toast.error(errMsg(e)); }
                          }} className="rounded-full border-sky-300 bg-transparent text-sky-700 hover:bg-sky-50 font-ui h-7 px-2.5 text-[11px]">
                            Hold Card
                          </Button>
                        )}
                        {(b.status === "confirmed" || b.status === "pending_payment") && (
                          changeFor === b.id ? (
                            <span data-testid={`change-vehicle-picker-${b.id}`} className="inline-flex items-center gap-1">
                              <select data-testid={`change-vehicle-select-${b.id}`} value={changeVehicleId} onChange={(e) => setChangeVehicleId(e.target.value)} className="border border-[#E5DED5] rounded-full px-2 py-1 text-xs font-ui bg-white max-w-[150px]">
                                <option value="">Vehicle…</option>
                                {vehicles.filter((v) => v.id !== b.vehicle_id).map((v) => (
                                  <option key={v.id} value={v.id}>{v.name || `${v.year} ${v.make} ${v.model}`}</option>
                                ))}
                              </select>
                              <label className="flex items-center gap-1 text-[10px] font-ui text-[#4A4A4A] whitespace-nowrap">
                                <input data-testid={`change-vehicle-free-${b.id}`} type="checkbox" checked={changeFree} onChange={(e) => setChangeFree(e.target.checked)} />
                                Free
                              </label>
                              <Button size="sm" data-testid={`change-vehicle-apply-${b.id}`} disabled={!changeVehicleId} onClick={() => changeVehicle(b.id)} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-ui h-7 px-2.5 text-[11px]">Apply</Button>
                              <Button size="sm" variant="outline" onClick={() => { setChangeFor(null); setChangeVehicleId(""); }} className="rounded-full font-ui h-7 px-2 text-[11px]">×</Button>
                            </span>
                          ) : (
                            <Button data-testid={`change-vehicle-button-${b.id}`} size="sm" variant="outline" onClick={() => setChangeFor(b.id)} className="rounded-full border-emerald-300 bg-transparent text-emerald-700 hover:bg-emerald-50 font-ui h-7 px-2.5 text-[11px]">
                              Change Vehicle
                            </Button>
                          )
                        )}
                      </div>
                      <p className="mt-2.5 text-[10px] text-muted-foreground font-ui leading-relaxed">Hold Card sends the guest a secure link to save a card for post-rental charges. Changing the vehicle issues a fresh contract for re-signing.</p>
                    </section>
                    <section className="bg-white border border-[#E5DED5] rounded-xl p-4 md:col-span-2">
                      <p className="font-ui text-xs font-semibold tracking-[0.2em] uppercase text-[#4A4A4A] mb-3">History & Lifecycle</p>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        {b.status === "confirmed" && b.return_submitted && (() => {
                          const checklistDone = b.fuel_return_level && b.return_photos_verified && (b.deposit_status || "held") !== "held";
                          return (
                            <Button
                              data-testid={`booking-complete-${b.id}`}
                              size="sm"
                              variant="outline"
                              disabled={!checklistDone}
                              title={checklistDone ? "Close out this rental" : "Finish the return checklist first: fuel level recorded, photos verified, deposit resolved"}
                              onClick={() => act(b.id, { status: "completed" }, "Rental completed")}
                              className="rounded-full border-emerald-300 bg-transparent text-emerald-700 hover:bg-emerald-50 font-ui h-7 px-2.5 text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Complete
                            </Button>
                          );
                        })()}
                        {b.invoice_reopened_at && b.status === "confirmed" && (
                          <Button data-testid={`booking-reclose-${b.id}`} size="sm" variant="outline" onClick={() => { if (!window.confirm("Re-close this booking? The invoice is finalized with any newly added charges and the guest receives the updated completion email.")) return; act(b.id, { status: "completed" }, "Booking re-closed — updated completion email sent"); }} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-ui h-7 px-2.5 text-[11px]">
                            Re-close Booking
                          </Button>
                        )}
                        {b.status === "confirmed" && !b.picked_up_at && (
                          <Button data-testid={`booking-picked-up-${b.id}`} size="sm" variant="outline" onClick={() => { if (!window.confirm(`Mark ${b.vehicle_name} as picked up by ${b.customer_name}? The vehicle stays blocked for the full rental window so it can't be overbooked.`)) return; act(b.id, { mark_picked_up: true }, "Marked as picked up — vehicle blacked out for the rental"); }} className="rounded-full border-sky-300 bg-transparent text-sky-700 hover:bg-sky-50 font-ui h-7 px-2.5 text-[11px]">
                            Mark Picked Up
                          </Button>
                        )}
                        {b.status === "confirmed" && (
                          <Button data-testid={`booking-manual-return-${b.id}`} size="sm" variant="outline" onClick={() => {
                            const accH = Number(b.late_accrual_hours) || 0;
                            if (accH > 0 && !(Number(b.late_fee) > 0)) {
                              const hrs = window.prompt(`Guest is ${accH}h overdue — accrued late fee ${fmtMoney(b.late_accrual_fee)} @ $25/hr.\nLate hours to bill (0 = waive, no fee):`, String(accH));
                              if (hrs === null) return;
                              const h = parseFloat(hrs) || 0;
                              act(b.id, { status: "completed", skip_return_checks: true, late_hours: h }, h > 0 ? `Rental closed — late fee $${(h * 25).toFixed(2)} applied, completion email & text sent` : "Rental closed — late fee waived, completion email & text sent");
                              return;
                            }
                            if (!window.confirm("Close this rental as a MANUAL rental return? Use for attended returns — photo, fuel and deposit checks are skipped, and the customer receives the completion email + SMS.")) return;
                            act(b.id, { status: "completed", skip_return_checks: true }, "Rental closed manually — completion email & text sent");
                          }} className="rounded-full border-[#1A1A1A]/30 bg-transparent text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white font-ui h-7 px-2.5 text-[11px]">
                            Rental Return
                          </Button>
                        )}
                        {b.reschedule_request?.status === "pending" && (
                          <div data-testid={`booking-reschedule-request-${b.id}`} className="w-full rounded-xl border border-sky-200 bg-sky-50 p-3 flex flex-wrap items-center gap-2">
                            <p className="text-xs font-ui text-sky-800 flex-1 min-w-[200px]">
                              <b>Reschedule requested:</b> {b.reschedule_request.new_start} → {b.reschedule_request.new_end}
                              <span className="block text-[10px] uppercase tracking-wide mt-0.5">{b.reschedule_request.tier === "free" ? "Tier: free" : "Tier: 20% fee applies"}</span>
                            </p>
                            <Button data-testid={`booking-reschedule-approve-${b.id}`} size="sm" variant="outline" className="rounded-full border-emerald-300 bg-transparent text-emerald-700 hover:bg-emerald-50 font-ui h-7 px-2.5 text-[11px]" onClick={async () => {
                              try { await api.post(`/admin/bookings/${b.id}/reschedule-request/decide`, { approve: true }); toast.success("Reschedule approved — dates moved, guest emailed"); load(); }
                              catch (e) { toast.error(errMsg(e)); }
                            }}>Approve</Button>
                            <Button data-testid={`booking-reschedule-decline-${b.id}`} size="sm" variant="outline" className="rounded-full border-red-300 bg-transparent text-red-700 hover:bg-red-50 font-ui h-7 px-2.5 text-[11px]" onClick={async () => {
                              if (!window.confirm("Decline this reschedule request? The guest is emailed that their original dates stand.")) return;
                              try { await api.post(`/admin/bookings/${b.id}/reschedule-request/decide`, { approve: false }); toast.success("Request declined — guest emailed"); load(); }
                              catch (e) { toast.error(errMsg(e)); }
                            }}>Decline</Button>
                          </div>
                        )}
                        {b.status === "confirmed" && (
                          <Button data-testid={`booking-reschedule-${b.id}`} size="sm" variant="outline" onClick={() => {
                            const n = (b.reschedule_history || []).length;
                            const tier = n === 0 ? "FREE if pickup is more than 24h away (otherwise 20% fee)" : n === 1 ? "carries a 20% reschedule fee" : "will AUTOMATICALLY CANCEL this booking per policy";
                            if (n >= 2 && !window.confirm(`This is the THIRD reschedule for ${b.booking_ref} — it will automatically CANCEL the booking and the deposit hold will be released. Continue?`)) return;
                            const ns = window.prompt(`New PICKUP date for ${b.booking_ref} (YYYY-MM-DD). Reschedule #${n + 1} ${tier}. Same ${b.days}-day length:`, b.start_date);
                            if (!ns) return;
                            const ne = window.prompt("New RETURN date (YYYY-MM-DD):", b.end_date);
                            if (!ne) return;
                            act(b.id, { new_start: ns, new_end: ne }, `Reschedule #${n + 1} processed → ${ns} to ${ne}`);
                          }} className="rounded-full border-sky-300 bg-transparent text-sky-700 hover:bg-sky-50 font-ui h-7 px-2.5 text-[11px]">
                            Reschedule{(b.reschedule_history || []).length > 0 ? ` (${(b.reschedule_history || []).length})` : ""}
                          </Button>
                        )}
                        {b.status === "confirmed" && (
                          <Button data-testid={`booking-extend-${b.id}`} size="sm" variant="outline" onClick={async () => {
                            const ne = window.prompt(`Extend ${b.booking_ref} (${b.vehicle_name}) — new RETURN date (YYYY-MM-DD), after ${b.end_date}:`, "");
                            if (!ne) return;
                            try {
                              const { data } = await api.post(`/admin/bookings/${b.id}/extend`, { new_end_date: ne });
                              toast.success(`Extended ${data.extra_days} day(s) → ${data.new_end_date} ($${Number(data.amount).toFixed(2)} recorded offline). Contract renewed — guest emailed/texted to re-sign; new dates blacked out.`);
                              load();
                            } catch (e) { toast.error(errMsg(e)); }
                          }} className="rounded-full border-violet-300 bg-transparent text-violet-700 hover:bg-violet-50 font-ui h-7 px-2.5 text-[11px]">
                            Extend
                          </Button>
                        )}
                        {b.status !== "cancelled" && (
                          <Button data-testid={`booking-edit-${b.id}`} size="sm" variant="outline" onClick={() => setEditFor(b)} title="Edit guest details, dates, times and locations" className="rounded-full border-[#C71E2E]/30 bg-transparent text-[#C71E2E] hover:bg-[#C71E2E]/10 font-ui h-7 px-2.5 text-[11px]">
                            <Pencil size={11} className="mr-1" /> Edit
                          </Button>
                        )}
                        {b.status === "confirmed" && (
                          <Button data-testid={`booking-hours-${b.id}`} size="sm" variant="outline" onClick={async () => {
                            const h = window.prompt(`Extend ${b.booking_ref} by how many HOURS? ($25/hr, 1-11, collected at return):`, "2");
                            if (!h) return;
                            try {
                              const { data } = await api.post(`/admin/bookings/${b.id}/extend-hours`, { hours: parseInt(h, 10) });
                              toast.success(`Extended ${data.hours} hr — $${Number(data.amount).toFixed(2)} added to the booking. Guest notified.`);
                              load();
                            } catch (e) { toast.error(errMsg(e)); }
                          }} className="rounded-full border-amber-300 bg-transparent text-amber-700 hover:bg-amber-50 font-ui h-7 px-2.5 text-[11px]">
                            +Hours
                          </Button>
                        )}
                        {b.status === "completed" && (
                          <Button data-testid={`booking-reopen-${b.id}`} size="sm" variant="outline" onClick={() => { if (!window.confirm("Re-open this invoice? The rental goes back to 'confirmed' so you can add penalties that were missed, charge the card on file or deduct from the deposit, then complete it again. The guest is emailed any new charges.")) return; act(b.id, { reopen_invoice: true }, "Invoice re-opened — add the missed penalties, then charge/deduct and complete again"); }} className="rounded-full border-amber-300 bg-transparent text-amber-700 hover:bg-amber-50 font-ui h-7 px-2.5 text-[11px]">
                            Re-open Invoice
                          </Button>
                        )}
                        {b.status === "cancelled" && (
                          <Button data-testid={`booking-uncancel-${b.id}`} size="sm" variant="outline" onClick={async () => {
                            if (!window.confirm(`Restore booking ${b.booking_ref}? It returns to its previous status, the deposit hold is restored, and the vehicle is blocked for those dates again.`)) return;
                            try { const { data } = await api.post(`/admin/bookings/${b.id}/uncancel`); toast.success(`Booking restored to ${data.status}`); load(); }
                            catch (e) { toast.error(errMsg(e)); }
                          }} className="rounded-full border-emerald-300 bg-transparent text-emerald-700 hover:bg-emerald-50 font-ui h-7 px-2.5 text-[11px]">
                            Undo Cancel
                          </Button>
                        )}
                        {b.status === "cancelled" && !b.cancellation_fee && (
                          <Button data-testid={`booking-cancel-fee-${b.id}`} size="sm" variant="outline" onClick={async () => {
                            if (!window.confirm(`Charge a 20% cancellation fee to ${b.customer_name}? The guest is emailed a secure payment link.`)) return;
                            try { const { data } = await api.post(`/admin/bookings/${b.id}/charge-cancellation-fee`); toast.success(`20% cancellation fee applied — $${data.cancellation_fee} — guest emailed payment link`); load(); }
                            catch (e) { toast.error(errMsg(e)); }
                          }} className="rounded-full border-amber-300 bg-transparent text-amber-700 hover:bg-amber-50 font-ui h-7 px-2.5 text-[11px]">
                            Charge 20% Fee
                          </Button>
                        )}
                        {b.status === "cancelled" && b.cancellation_fee > 0 && (
                          <span data-testid={`booking-cancel-fee-applied-${b.id}`} className="text-[10px] font-ui font-semibold uppercase tracking-wide text-amber-700 border border-amber-200 bg-amber-50 rounded-full px-2 py-0.5">20% fee applied</span>
                        )}
                      </div>
                      <BookingHistory b={b} />
                      {(b.payment_method === "wire" || (b.wire_proof_events || []).length > 0) && <WireProofPanel b={b} />}
                      <div className="mt-4 pt-3 border-t border-red-100 flex flex-wrap items-center gap-2">
                        <p className="text-[10px] font-ui uppercase tracking-wide text-red-700/70 mr-1">Danger zone</p>
                        {b.status !== "cancelled" && (
                          <Button data-testid={`booking-cancel-${b.id}`} size="sm" variant="outline" onClick={() => act(b.id, { status: "cancelled" }, "Booking cancelled")} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-red-50 hover:text-red-700 font-ui h-7 px-2.5 text-[11px]">
                            Cancel
                          </Button>
                        )}
                        <Button data-testid={`booking-delete-${b.id}`} size="sm" variant="outline" onClick={async () => {
                          if (!window.confirm(`Delete booking ${b.booking_ref} permanently? This cannot be undone.`)) return;
                          try { await api.delete(`/admin/bookings/${b.id}`); toast.success("Booking deleted"); load(); }
                          catch (e) { toast.error(errMsg(e)); }
                        }} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-red-50 hover:text-red-700 font-ui h-7 px-2.5 text-[11px]">
                          Delete
                        </Button>
                      </div>
                    </section>
                  </div>
                </TableCell>
              </TableRow>
            )}
            </Fragment>
            );
          })}
          {quotes.map((q) => {
            const qst = q.booking_id ? (q.status === "accepted" ? "ACCEPTED" : "CONVERTED") : (q.expires_at && new Date(q.expires_at) < new Date() ? "EXPIRED" : "QUOTED");
            return (
              <TableRow key={q.id} data-testid={`quote-row-${q.id}`} className="bg-amber-50/40">
                <TableCell colSpan={99}>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-ui text-sm">
                    <span className="font-semibold text-[#1A1A1A]">{q.quote_ref}</span>
                    <span data-testid={`quote-status-${q.id}`} className={`border rounded-full px-2 py-0.5 text-[10px] font-ui whitespace-nowrap ${qst === "QUOTED" ? "text-amber-800 border-amber-300 bg-amber-50" : qst === "EXPIRED" ? "text-stone-500 border-stone-200 bg-stone-100" : "text-emerald-700 border-emerald-200 bg-emerald-50"}`}>{qst}</span>
                    <span>{q.customer_name}{q.customer_email ? ` · ${q.customer_email}` : ""}</span>
                    <span className="text-xs text-muted-foreground">{q.vehicle_name} · {q.start_date} → {q.end_date}</span>
                    <span className="font-semibold">${Number(q.total || 0).toFixed(2)}</span>
                    <span className="flex-1" />
                    {qst === "QUOTED" && (
                      <>
                        <button data-testid={`quote-convert-${q.id}`} onClick={async () => { try { const { data } = await api.post(`/admin/quotes/${q.id}/convert`); toast.success(`Quote ${q.quote_ref} → booking ${data.booking.booking_ref}`); load(); api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {}); } catch (err) { toast.error(errMsg(err)); } }} className="text-sm font-ui text-emerald-700 hover:text-emerald-900 transition-colors duration-200 mr-3">Convert</button>
                        <button data-testid={`quote-resend-${q.id}`} onClick={async () => { try { await api.post(`/admin/quotes/${q.id}/resend`); toast.success(`Quote ${q.quote_ref} re-sent`); } catch (err) { toast.error(errMsg(err)); } }} className="text-sm font-ui text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-3">Resend</button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
